import { Observable } from 'rxjs';
import {
  PipeTransform,
  Signal,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import {
  UiPopupHideStrategyType,
  ButtonIconPositionType,
  IconSizeType,
  TextInputType,
  DateSelectionType,
  DropdownSelectedOptionStrategyType,
  FileUploadUiType,
  SpreadsheetAllowedValuesType,
  IconType,
  TPageLayout,
  TPageLayoutAreaSizes,
  TPageLayoutRouteOutlets,
} from '../types';
import { PrimitiveType, ClassConstructor, PropExtend } from '../types';
import { StateEnum } from '../enums/state.enums';
import { ToastPositionEnum, ToastTypeEnum } from '../enums';

export interface IUiOption {
  label?: string;
  value?: string | number;
  data?: unknown;
  group?: string;
  isSelectable?: boolean;
  isSelected?: boolean;
  isIndicated?: boolean;
  isDeleted?: boolean;
}

export interface IUiLocationTag {
  tag: string;
  writeMode: 'replace' | 'append' | 'prepend';
}

export interface IUiSpreadSheetCell<T> {
  columnIndex?: number;
  rowIndex?: number;
  formattedValue: Signal<string | number | undefined>;
  el?: HTMLElement;
  state?: WritableSignal<StateEnum | undefined>;
  selected?: boolean;
  marked?: boolean;
  editing?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  classes: Signal<string>;
  annotations?: string;
  tag?: string;
  dataItem?: T;
}

//#endregion

//#region Inherited Directives

export interface IUiSettings {}

export interface IUiLayoutSettings {
  instanceId?: number;
  cols?: number;
  rows?: number;
  colGap?: number;
  rowGap?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export interface IUiControlSettings extends IUiLayoutSettings {
  uid?: string | number;
  hide?: unknown;
  label?: string;
  labelFocused?: string;
  labelTip?: string;
  labelPosition?: 'left' | 'top' | 'right' | 'bottom' | 'embedded';
  hideLabel?: boolean;
  defaultIcon?: IconType;
  hideDefaultIcon?: boolean;
  tag?: string;
  valueOutput?: 'signal' | 'plain';
  disabled?: boolean;
}

export interface IUiInputSettings extends IUiControlSettings {
  placeholder?: string;
  placeholderPersists?: boolean;
  footerMessage?: string;
  options?: Signal<any> | Array<any> | (() => Array<any>);
  allowSameValue?: boolean;
  stateless?: boolean;
  audited?: boolean;
  showClearButton?: boolean;
  defaultIcon?: IconType;
  defaultIconSlot?: 'left' | 'right';
}

export interface IUiPopupSettings extends IUiControlSettings {
  hideStrategy?: UiPopupHideStrategyType;
  skipOutsideClick?: number;
  renderInRoot?: boolean;
  renderInElement?: HTMLElement;
  forceHideOnToggle?: boolean;
}

//#endregion

//#region Ui Directives

export interface IUiMultiSelectSettings {
  inputElementRef?: string;
  listElementRef?: string;
  listField?: string;
}

//#endregion

//#region Input

export interface IUiIconControlSettings extends IUiControlSettings {
  icon?: IconType;
  iconOnly?: boolean;
  iconPosition?: ButtonIconPositionType;
  iconSize?: string;
  iconColor?: string;
  iconCssClass?: string;
}

export interface IUiListSettings<T> extends IUiControlSettings {
  itemValueKey?: string;
  itemIdKey?: string;
  itemSortingKey?: string;
  itemHiddenKey?: string;
  itemDeleteKey?: string;
  reOrdering?: boolean;
  items?: Signal<T> | Array<T> | (() => Array<T>);
  metaData?: any;
}

export interface IUiTreeSettings<T> extends IUiControlSettings {
  nodeNameKey?: string;
  nodeValueKey?: string;
  nodeIconKey?: string;
  nodeChildrenKeys?: Array<string>;
  nodes?: Signal<T> | Array<T> | (() => Array<T>);
}

export interface ICheckSettings extends IUiInputSettings {
  isToggle?: boolean;
  preventChecked?: boolean;
}

export interface IDateSettings extends IUiInputSettings {
  selection?: DateSelectionType;
  format?: string;
  yearsInPage?: number;
  defaultDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  indicatedDates?: Array<Date>;
  hideDayButton?: boolean;
  hideMonthButton?: boolean;
  hideYearButton?: boolean;
  hideDayPreviousButton?: boolean;
  hideDayNextButton?: boolean;
  hideMonthPreviousButton?: boolean;
  hideMonthNextButton?: boolean;
  hideYearPreviousButton?: boolean;
  hideYearNextButton?: boolean;
  hideDayNames?: boolean;
  autoNavigateToLastSelectedDate?: boolean;
  dayButtonLabelFormat?: string;
  monthButtonLabelFormat?: string;
  popup?: IUiPopupSettings;
}

export interface IMultiDateSettings extends IDateSettings {
  shrinkCount?: number;
}

export interface IRadioGroupSettings extends IUiInputSettings {
  defaultSelect?: boolean;
  disabled?: boolean;
  optionLabel?: string;
  optionValue?: string;
  radioSize?: string | number;
}

export interface IDropdownSettings extends IUiInputSettings {
  //Options
  optionLabel?: string;
  optionValue?: string;
  optionDeleted?: string;
  optionDisabled?: string;
  optionGroup?: string;
  optionsSorting?: boolean | string;
  optionsFiltering?: boolean;
  optionsAsyncFn?: () => Observable<Array<unknown>>;
  optionsAsyncMerging?: boolean;
  optionsDebounce?: number | 'enter';
  optionsAsyncMinQuery?: number;
  selectedOptionStrategy?: DropdownSelectedOptionStrategyType;
  customOptionTmpl?: TemplateRef<any>;
  optionMapping?: IUiOption;
  includeEventOnSelectOutput?: boolean;

  //Input
  clearInputOnFocus?: boolean;
  clearInputOnSelect?: boolean;
  storeOnlyValue?: boolean;
  allowMultiple?: boolean;

  //Icons
  loadingIcon?: IconType;

  //Popup
  popup?: IUiPopupSettings;
}

export interface IMultiDropdownSettings extends IDropdownSettings {
  shrinkCount?: number;
}

export interface IFileUploadSettings extends IUiInputSettings {
  uiType?: FileUploadUiType;
  defaultIcon?: IconType | undefined;
  allowMultipleFiles?: boolean;
  allowedFileTypes?: string;
  showLabel?: boolean;
  showFooter?: boolean;
  autoClearAfterUpload?: boolean;
}

export interface ILocationSettings extends IDropdownSettings {
  valueFormat?: string;
  fieldMap?: Array<{ field: string; tags: Array<IUiLocationTag> }>;
  markerLabel?: string;
}

export interface INumberSettings extends IUiInputSettings {
  prefix?: string;
  suffix?: string;
  onlyWholeNumbers?: boolean;
  permitNegative?: boolean;
  permitZero?: boolean;
  permitShortcuts?: boolean;
  abbr?: boolean;
  decimalAmount?: number;
  thousandsSeparator?: string;
  percentAsIs?: boolean;
}

export interface ISpreadsheetSettings<T> extends IUiControlSettings {
  inputMode?: 'directly' | 'popup';

  //Toolbar
  showNumberFormat?: boolean;
  showCloseToggle?: boolean;

  numberFormat?: boolean;

  //Sheet
  defaultRowQuantity?: number;
  defaultColumnQuantity?: number;
  defaultSelectedCell?: string;
  columnTitles?: Array<string>;
  rowTitles?: Array<string>;
  summaryTitle?: string;
  initialCellValues?: Array<PrimitiveType>;

  //All Cells
  cellInstance?: ClassConstructor<unknown>;
  allowedValues?: SpreadsheetAllowedValuesType;
  idField?: keyof T;
  valueField?: keyof T;
  stateField?: keyof T;
  disabledField?: keyof T;
  deletedField?: keyof T;
  cellCssField?: keyof T;
  currencyField?: keyof T;
  columnIndexField?: keyof T;
  rowIndexField?: keyof T;
  tagPrefix?: string;
  numberFormattingType?:
    'currency' | 'percent' | 'ratio' | 'number' | 'abbreviated';
  numberFormattingPrecision?: string;
  percentageAsIs?: boolean;
  conditionalFormatting?: (value: unknown) => string;
  stateless?: boolean;
}

export interface ITextSettings extends IUiInputSettings {
  multiline?: boolean;
  showCharCount?: boolean;
  type?: TextInputType;
  disableExpandToggle?: boolean;
  maxExpandedHeight?: number;
  startExpanded?: boolean;
}

export interface IRichTextSettings extends IUiInputSettings {
  allowStylingOnNonEditable?: boolean;
  anchorEditButtons?: Array<'anchorEdit' | 'anchorRemove'>;
  apiKey?: string;
  app?: string;
  attribution?: boolean;
  autoStart?: boolean;
  autofocus?: boolean;
  charCounterCount?: boolean;
  charCounterMax?: number;
  cleanReduntantStyleOnGet?: boolean;
  clearFormatOnEnterNewLine?: boolean;
  codeBeautifierOptions?: {
    end_with_newline: boolean;
    indent_inner_html: boolean;
    extra_liners: Array<string>;
    brace_style: 'collapse' | 'expand' | 'end-expand' | 'none';
    indent_char: string;
    indent_size: number;
    wrap_line_length: number;
  };
  codeMirror?: boolean;
  codeMirrorOptions?: {
    indeentWithTabs: boolean;
    lineNumbers: boolean;
    lineWrapping: boolean;
    mode: string;
    tabMode: 'shift' | 'indent' | 'classic';
    tabSize: number;
  };
  codeViewKeepActiveButtons?: Array<string>;
  colorsBackground?: Array<string>;
  colorsButtons?: Array<string>;
  colorsHEXInput?: boolean;
  colorsStep?: number;
  colorsText?: Array<string>;
  customButtons?: Array<{
    title: string;
    icon: string;
    focus?: boolean;
    undo?: boolean;
    refreshAfterCallback?: boolean;
    shortcut?: {
      letter: string;
      shift_key?: boolean;
    };
    callback: (...args: any[]) => void;
  }>;
  direction?: 'auto' | 'ltr' | 'rtl';
  disableRightClick?: boolean;
  docId?: string;
  documentReady?: boolean;
  dragInline?: boolean;
  editInPopup?: boolean;
  editor?: any;
  editorClass?: string;
  embedlyEditButtons?: Array<string>;
  embedlyInsertButtons?: Array<string>;
  embedlyKey?: string;
  embedlyScriptPath?: string;
  emoticonsButtons?: Array<string>;
  emoticonsSet?: string;
  emoticonsStep?: number;
  emoticonsUseImage?: boolean;
  enableMatchCase?: boolean;
  enableMatchWholeWord?: boolean;
  enableTableSelection?: boolean;
  showFindAndReplace?: boolean;
  enter?: 'P' | 'DIV' | 'BR';
  entities?: boolean;
  events?: { [key: string]: (...args: any[]) => void };
  exportPageBreak?: boolean;
  faButtons?: Array<string>;
  fileAllowedTypes?: Array<string>;
  fileInsertButtons?: Array<string>;
  filesManagerAllowedTypes?: Array<string>;
  filesManagerMaxSize?: number;
  filesManagerUploadParams?: { [key: string]: any };
  filesManagerUploadToS3?: boolean;
  filesManagerUploadURL?: string;
  fileMaxSize?: number;
  filestackOptions?: {
    filestackAPI?: string;
    pickerOptions?: { [key: string]: any };
    uploadToFilestackOnly?: boolean;
  };
  fileUpload?: boolean;
  fileUploadMethod?: 'POST' | 'PUT' | 'PATCH';
  fileUploadParam?: string;
  fileUploadParams?: { [key: string]: any };
  fileUploadToS3?: boolean;
  fileUploadURL?: string;
  fileUseSelectedText?: boolean;
  fontAwesomeSets?: string;
  fontAwesomeTemplate?: string;
  fontFamily?: Array<string>;
  fontFamilyDefaultSelection?: string;
  fontFamilySelection?: string;
  fontSize?: Array<string>;
  fontSizeDefaultSelection?: string;
  fontSizeSelection?: string;
  fontSizeUnit?: string;
  formEditButtons?: Array<string>;
  formMultipleStyles?: boolean;
  formStyles?: { [key: string]: string };
  formUpdateButtons?: Array<string>;
  fullPage?: boolean;
  height?: number;
  heightMax?: number;
  heightMin?: number;
  helpSets?: Array<string>;
  htmlAllowComments?: boolean;
  htmlAllowedAttrs?: Array<string>;
  htmlAllowedEmptyTags?: Array<string>;
  htmlAllowedStyleProps?: Array<string>;
  htmlAllowedTags?: Array<string>;
  htmlDoNotWrapTags?: Array<string>;
  htmlExecuteScripts?: boolean;
  htmlIgnoreCSSProperties?: Array<string>;
  htmlRemoveTags?: Array<string>;
  htmlSimpleAmpersand?: boolean;
  htmlUntouched?: boolean;
  iconsTemplate?: string;
  iframe?: boolean;
  iframeDefaultStyle?: string;
  iframeStyle?: string;
  iframeStyleFiles?: Array<string>;
  imageAddNewLine?: boolean;
  imageAllowedTypes?: Array<string>;
  imageAltButtons?: Array<
    'imageAlt' | 'imageSize' | 'imageStyle' | 'imageRemove'
  >;
  imageCORSProxy?: string;
  imageDefaultAlign?: string;
  imageDefaultDisplay?: string;
  imageDefaultMargin?: number;
  imageDefaultWidth?: number;
  imageEditButtons?: Array<string>;
  imageInsertButtons?: Array<string>;
  imageManagerDeleteMethod?: 'POST' | 'DELETE' | 'GET' | 'PUT' | 'PATCH';
  imageManagerDeleteParams?: { [key: string]: any };
  imageManagerDeleteURL?: string;
  imageManagerLoadMethod?: 'POST' | 'DELETE' | 'GET' | 'PUT' | 'PATCH';
  imageManagerLoadParams?: { [key: string]: any };
  imageManagerLoadURL?: string;
  imageManagerPageSize?: number;
  imageManagerPreloader?: boolean;
  imageManagerScrollOffset?: number;
  imageManagerToggleTags?: Array<string>;
  imageMaxSize?: number;
  imageMinWidth?: number;
  imageMove?: boolean;
  imageMultipleStyles?: boolean;
  imageOutputSize?: boolean;
  imagePaste?: boolean;
  imagePasteProcess?: (image: File) => File;
  imageResize?: boolean;
  imageResizeWithPercent?: boolean;
  imageRoundPercent?: boolean;
  imageSizeButtons?: Array<string>;
  imageSplitHTML?: boolean;
  imageStyles?: { [key: string]: string };
  imageTUIOptions?: { [key: string]: any };
  imageTextNear?: boolean;
  imageUpload?: boolean;
  imageUploadMethod?: 'POST' | 'PUT' | 'PATCH';
  imageUploadParam?: string;
  imageUploadParams?: { [key: string]: any };
  imageUploadRemoteUrls?: boolean;
  imageUploadToS3?: boolean;
  imageUploadURL?: string;
  indentMargin?: number;
  initOnClick?: boolean;
  inlineClasses?: { [key: string]: string };
  inlineStyles?: { [key: string]: string };
  keepFormatOnDelete?: boolean;
  Key?: string;
  language?: string;
  lineBreakerHorizontalOffset?: number;
  lineBreakerOffset?: number;
  lineBreakerTags?: Array<string>;
  lineHeights?: { [key: string]: string };
  linkAlwaysBlank?: boolean;
  linkAlwaysNoFollow?: boolean;
  linkAttributes?: { [key: string]: string };
  linkAutoPrefix?: string;
  linkConvertEmailAddress?: boolean;
  linkEditButtons?: Array<'linkOpen' | 'linkEdit' | 'linkRemove'>;
  linkInsertButtons?: Array<string>;
  linkList?: Array<{ text: string; href: string }>;
  linkMultipleStyles?: boolean;
  linkNoOpener?: boolean;
  linkNoReferrer?: boolean;
  linkStyles?: { [key: string]: string };
  linkText?: boolean;
  listAdvancedTypes?: boolean;
  multiLine?: boolean;
  paragraphDefaultSelection?: string;
  paragraphFormat?: { [key: string]: string };
  paragraphFormatSelection?: string;
  paragraphMultipleStyles?: boolean;
  paragraphStyles?: { [key: string]: string };
  pasteAllowLocalImages?: boolean;
  pasteAllowedStyleProps?: Array<string>;
  pasteDeniedAttrs?: Array<string>;
  pasteDeniedTags?: Array<string>;
  pastePlain?: boolean;
  placeholderText?: string;
  pluginsDisabled?: Array<string>;
  pluginsEnabled?: Array<string>;
  proportionalTableResize?: boolean;
  quickInsertButtons?: Array<string>;
  quickInsertEnabled?: boolean;
  quickInsertTags?: Array<string>;
  requestHeaders?: { [key: string]: string };
  requestWithCORS?: boolean;
  requestWithCredentials?: boolean;
  saveInterval?: number;
  saveMethod?: 'POST' | 'PUT' | 'PATCH';
  saveParam?: string;
  saveParams?: { [key: string]: any };
  saveURL?: string;
  scrollableContainer?: string;
  shortcutsEnabled?: boolean;
  shortcutsHint?: boolean;
  specialCharButtons?: Array<string>;
  specialCharactersSets?: { [key: string]: Array<string> };
  spellcheck?: boolean;
  tabIndex?: number;
  tabSpaces?: number;
  tableCellMultipleStyles?: boolean;
  tableCellStyles?: { [key: string]: string };
  tableColors?: Array<string>;
  tableColorsButtons?: Array<string>;
  tableColorsStep?: number;
  tableDefaultAlign?: string;
  tableDefaultBGColor?: string;
  tableDefaultBorderColor?: string;
  tableDefaultBorderStyle?: string;
  tableDefaultBorderWidth?: number;
  tableDefaultHeight?: number;
  tableDefaultWidth?: number;
  tableEditButtons?: Array<string>;
  tableInsertButtons?: Array<string>;
  tableInsertHelper?: string;
  tableInsertHelperOffset?: number;
  tableInsertMaxSize?: number;
  tableMultipleStyles?: boolean;
  tableResizer?: boolean;
  tableResizerOffset?: number;
  tableResizingLimit?: number;
  tableStyles?: { [key: string]: string };
  theme?: string;
  toolbarBottom?: boolean;
  toolbarButtons?: any;
  toolbarButtonsMD?: Array<string>;
  toolbarButtonsSM?: Array<string>;
  toolbarButtonsXS?: Array<string>;
  toolbarContainer?: string;
  toolbarInline?: boolean;
  toolbarSticky?: boolean;
  toolbarStickyOffset?: number;
  toolbarVisibleWithoutSelection?: boolean;
  tooltips?: boolean;
  typingTimer?: number;
  useClasses?: boolean;
  username?: string;
  videoAllowedProviders?: Array<string>;
  videoAllowedTypes?: Array<string>;
  videoDefaultAlign?: string;
  videoDefaultDisplay?: string;
  videoDefaultWidth?: number;
  videoEditButtons?: Array<string>;
  videoInsertButtons?: Array<string>;
  videoMaxSize?: number;
  videoMove?: boolean;
  videoResize?: boolean;
  videoResponsive?: boolean;
  videoSizeButtons?: Array<string>;
  videoSplitHTML?: boolean;
  videoTextNear?: boolean;
  videoUpload?: boolean;
  videoUploadMethod?: 'POST' | 'PUT' | 'PATCH';
  videoUploadParam?: string;
  videoUploadParams?: { [key: string]: any };
  videoUploadToS3?: boolean;
  videoUploadURL?: string;
  width?: number;
  wordAllowedStyleProps?: Array<string>;
  wordCounterCount?: boolean;
  wordCounterMax?: number;
  wordDeniedAttrs?: Array<string>;
  wordDeniedTags?: Array<string>;
  wordExportFileName?: string;
  wordPasteKeepFormatting?: boolean;
  wordPasteModal?: boolean;
  zIndex?: number;
}

//#endregion

//#region Layout

export interface IModalSettings extends IUiControlSettings {
  hasBackdrop?: boolean;
  enableCloseOnBackdrop?: boolean;
  hideStrategy?: UiPopupHideStrategyType;
  useRouterOutlet?: boolean;
  useNgContent?: boolean;
  textFormatter?: 'plain' | 'json' | 'html';
  width?: number;
  height?: number;
  actions?: Array<{
    label: string;
    icon?: string;
    disabled?: boolean;
    action: () => void;
  }>;
}

export interface IPageSettings extends IUiLayoutSettings {}

export interface IPageLayoutSettings extends IUiControlSettings {
  type?: TPageLayout;
  areaSizes?: TPageLayoutAreaSizes;
  handles?: string;
  outlets?: TPageLayoutRouteOutlets;
  includeScrollbarArea?: boolean;
  useRouterOutlet?: boolean;
  useNgContent?: boolean;
}

export interface IPageToolbarConfig {
  showRolesTooltip?: boolean;
  options: Array<{
    label: string;
    value: string;
    icon?: IconType;
  }>;
}

export interface ISectionSettings extends IUiControlSettings {
  headerIsHidden?: boolean;
  titleIsHidden?: boolean;
  noContentMsg?: string;
  isMinimized?: boolean;
  isCollapsible?: boolean;
  collapsePosition?: 'right' | 'left';
  isRemovable?: boolean;
  header?: IUiLayoutSettings;
  content?: IUiLayoutSettings;
  scope?: string;
  useRouterOutlet?: boolean;
  useNgContent?: boolean;
}

export interface ISectionGroupSettings extends IUiControlSettings {
  isMinimized?: boolean;
  canMinimize?: boolean;
  useRouterOutlet?: boolean;
  useNgContent?: boolean;
}

//#endregion

//#region Presentation

export interface IChipSettings extends IUiControlSettings {
  label?: string;
  tag?: string;
}

export interface IToastSettings {
  type: ToastTypeEnum;
  message: string;
  pinned?: boolean;
  hideDelay?: number;
  position?: ToastPositionEnum;
  closeAnimationDelay?: number;
  useDynamicInjection?: boolean;
  clickFn?: (args: any) => void;
}

//#endregion

//#region Tools

export interface IHistorySettings {
  url: string;
  field?: string;
  buttonCssClass?: string;
  valueFormat: 'date' | 'number' | 'string' | 'boolean' | 'enum' | 'function';
  valueFormatter?: (value: unknown) => string;
  enumMapping?: {
    label: string;
    value: string;
  };
  enumValues?: Array<any> | Signal<Array<any>>;
  zeroToDash?: boolean;
}

//#endregion
